import { useState } from 'react';
import { ThemeIcon, Badge, Tooltip, Text, Progress } from '@mantine/core';
import {
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import {
  Calendar,
  Users,
  CheckCircle,
  FileText,
} from 'lucide-react';
import type { HealthStatCards, HealthStatCard } from '../../../../types';
import classes from './HealthStatCardsRow.module.css';

export type StatCardKey = 'sessions' | 'participants' | 'attendance' | 'satisfaction';

interface HealthStatCardsRowProps {
  statCards: HealthStatCards;
  selectedCard?: StatCardKey | null;
  onSelectCard?: (key: StatCardKey | null) => void;
}

const CARD_ICONS: Record<StatCardKey, React.ReactNode> = {
  sessions: <Calendar size={18} />,
  participants: <Users size={18} />,
  attendance: <FileText size={18} />,
  satisfaction: <CheckCircle size={18} />,
};

const CARD_ORDER: StatCardKey[] = ['sessions', 'participants', 'attendance', 'satisfaction'];

const ISSUE_COLOR_MAP: Record<string, string> = {
  red: 'var(--medley-flag-text, #E07B4C)',
  orange: 'var(--medley-flag-text, #E07B4C)',
  green: 'var(--medley-green-success, #4A7C59)',
  gray: 'var(--mantine-color-gray-5)',
};

/** Map API badge color to a CSS class override for consistent pill styling */
function getBadgeClassName(color: string | null | undefined): string | undefined {
  if (color === 'red' || color === 'orange') return classes.badgeFlagged;
  if (color === 'green') return classes.badgePositive;
  return undefined;
}

/** Parse a "3/6" style value into a 0-100 percentage, or null if unparseable */
function parseProgressPercent(value: string): number | null {
  const match = value.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (!match) return null;
  const completed = parseInt(match[1], 10);
  const total = parseInt(match[2], 10);
  if (total === 0) return 0;
  return Math.round((completed / total) * 100);
}

/** Extract flagged count from a badge label like "1 Flagged" */
function parseFlaggedCount(label: string | null): number {
  if (!label) return 0;
  const match = label.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
}

function StatCardBadge({ card, cardKey }: { card: HealthStatCard; cardKey: StatCardKey }) {
  // Satisfaction card: arrow icon badge with trend direction
  if (cardKey === 'satisfaction') {
    const color = card.badgeColor ?? 'gray';
    const TrendIcon = card.trendDirection === 'up' ? TrendingUp : card.trendDirection === 'down' ? TrendingDown : Minus;
    return (
      <Tooltip label={card.trendTooltip ?? ''} disabled={!card.trendTooltip} withArrow>
        <Badge
          size="sm"
          variant="light"
          color="gray"
          radius="sm"
          leftSection={<TrendIcon size={14} />}
          style={{ cursor: 'default' }}
          className={getBadgeClassName(color)}
          tabIndex={0}
          tt="none"
        >
          {card.trendDirection === 'up' ? 'Up' : card.trendDirection === 'down' ? 'Down' : 'Stable'}
        </Badge>
      </Tooltip>
    );
  }

  // Attendance card: trend percentage badge with tooltip
  if (cardKey === 'attendance' && card.badgeLabel) {
    const color = card.badgeColor ?? 'gray';
    return (
      <Tooltip label={card.trendTooltip ?? ''} disabled={!card.trendTooltip} withArrow>
        <Badge
          size="sm"
          variant="light"
          color="gray"
          radius="sm"
          className={getBadgeClassName(color)}
          tabIndex={0}
          tt="none"
        >
          {card.badgeLabel}
        </Badge>
      </Tooltip>
    );
  }

  // Sessions/Participants: flagged count badge (sentence case)
  if (card.badgeLabel) {
    const count = parseFlaggedCount(card.badgeLabel);
    return (
      <Badge
        size="sm"
        variant="light"
        color="gray"
        radius="sm"
        className={getBadgeClassName(card.badgeColor)}
        tt="none"
      >
        {count > 0 ? `${count} Flagged` : card.badgeLabel}
      </Badge>
    );
  }

  return null;
}

export function HealthStatCardsRow({ statCards, selectedCard: controlledSelected, onSelectCard }: HealthStatCardsRowProps) {
  const [internalSelected, setInternalSelected] = useState<StatCardKey | null>(null);
  const selectedCard = controlledSelected !== undefined ? controlledSelected : internalSelected;

  if (!statCards) return null;

  const handleCardClick = (key: StatCardKey) => {
    const next = selectedCard === key ? null : key;
    if (onSelectCard) {
      onSelectCard(next);
    } else {
      setInternalSelected(next);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, key: StatCardKey) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick(key);
    }
  };

  return (
    <div className={classes.statCardsSection}>
      <div className={classes.cardsGrid}>
        {CARD_ORDER.map(key => {
          const card = statCards[key];
          const icon = CARD_ICONS[key];
          const isSelected = selectedCard === key;
          const isSessionsCard = key === 'sessions';
          const progressPercent = isSessionsCard ? parseProgressPercent(card.value) : null;
          const flaggedCount = isSessionsCard ? parseFlaggedCount(card.badgeLabel) : 0;

          return (
            <div
              key={key}
              className={`${classes.statCard} ${isSelected ? classes.statCardSelected : ''}`}
              role="button"
              tabIndex={0}
              aria-label={`${card.label}: ${card.value}. ${card.issueSummary}`}
              aria-pressed={isSelected}
              onClick={() => handleCardClick(key)}
              onKeyDown={e => handleKeyDown(e, key)}
            >
              <div className={classes.cardTop}>
                <ThemeIcon color="gray" size={32} radius="md" variant="light">
                  {icon}
                </ThemeIcon>
                {isSessionsCard ? (
                  flaggedCount > 0 ? (
                    <Badge
                      size="sm"
                      variant="light"
                      color="gray"
                      radius="sm"
                      className={classes.badgeFlagged}
                      tt="none"
                    >
                      {flaggedCount} Flagged
                    </Badge>
                  ) : null
                ) : (
                  <StatCardBadge card={card} cardKey={key} />
                )}
              </div>

              {isSessionsCard ? (
                <>
                  <div className={classes.cardMetricRow}>
                    <span className={classes.cardValue}>{card.value}</span>
                    {progressPercent != null && (
                      <Progress
                        value={progressPercent}
                        color="green"
                        size="sm"
                        radius="xl"
                        className={classes.cardProgress}
                      />
                    )}
                  </div>
                  <div className={classes.cardLabel}>{card.label}</div>
                </>
              ) : (
                <>
                  {card.valueTooltip ? (
                    <Tooltip
                      label={card.valueTooltip}
                      withArrow
                      position="bottom"
                      color="dark"
                      multiline
                      w={320}
                      styles={{ tooltip: { whiteSpace: 'pre-line' } }}
                      events={{ hover: true, focus: true, touch: true }}
                    >
                      <div
                        className={classes.cardValue}
                        tabIndex={0}
                        role="note"
                        aria-label={card.valueTooltip.replace('\n', '; ')}
                      >
                        {card.value}
                      </div>
                    </Tooltip>
                  ) : (
                    <div className={classes.cardValue}>{card.value}</div>
                  )}
                  <div className={classes.cardLabel}>{card.label}</div>
                </>
              )}

              <div
                className={classes.cardIssue}
                style={{ color: ISSUE_COLOR_MAP[card.issueColor] || ISSUE_COLOR_MAP.gray }}
              >
                {card.issueSummary}
              </div>
            </div>
          );
        })}
      </div>

      {!selectedCard && (
        <Text className={classes.promptText}>
          Click any status card to view more
        </Text>
      )}
    </div>
  );
}
